import React, { useState, useEffect } from 'react';
import './Admin.css';
import { db, storage } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DeliveryFilesManagement from './DeliveryFilesManagement';
import CertificationManagement from './CertificationManagement';

const Admin = () => {
  // 로그인 상태 관리
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  
  const [koPosts, setKoPosts] = useState([]);
  const [enPosts, setEnPosts] = useState([]);
  const [koEProcurementPosts, setKoEProcurementPosts] = useState([]);
  const [enEProcurementPosts, setEnEProcurementPosts] = useState([]);
  const [legacyPosts, setLegacyPosts] = useState([]);
  const [legacyEProcurementPosts, setLegacyEProcurementPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('ko-posts'); // 'ko-posts', 'en-posts', 'ko-e-procurement', 'en-e-procurement', 'legacy-posts', 'legacy-e-procurement'
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postCategoryFilter, setPostCategoryFilter] = useState('all'); // all | catalog | technical
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    pdfFileName: '',
    pdfUrl: '',
    password: '',
    language: 'ko', // 언어 추가
    category: 'catalog' // 자료실 카테고리 (catalog | technical)
  });

  const getCollectionNameByTab = (tab) => {
    if (tab === 'ko-posts') return 'ko-posts';
    if (tab === 'en-posts') return 'en-posts';
    if (tab === 'ko-e-procurement') return 'ko-e-procurement';
    if (tab === 'en-e-procurement') return 'en-e-procurement';
    if (tab === 'legacy-posts') return 'posts';
    if (tab === 'legacy-e-procurement') return 'e-procurement';
    return 'posts';
  };

  const uploadPdfToStorage = async ({ file, folder }) => {
    const safeName = (file?.name || 'file').replace(/[^\w.\-() ]/g, '_');
    const path = `uploads/${folder}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type || 'application/pdf' });
    const url = await getDownloadURL(storageRef);
    return { url, path };
  };

  // Firebase에서 게시글 실시간 로드
  useEffect(() => {
    // 기존 posts 컬렉션과 새로운 ko-posts 컬렉션 모두 로드
    const koPostsRef = collection(db, 'ko-posts');
    const koPostsQuery = query(koPostsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeKoPosts = onSnapshot(koPostsQuery, (snapshot) => {
      const koPostsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setKoPosts(koPostsData);
      console.log('한글 자료실 게시글:', koPostsData.length, '개');
    });

    // 기존 posts 컬렉션 로드 (별도 탭으로 관리)
    const legacyPostsRef = collection(db, 'posts');
    const legacyPostsQuery = query(legacyPostsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeLegacyPosts = onSnapshot(legacyPostsQuery, (snapshot) => {
      const legacyPostsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLegacyPosts(legacyPostsData);
      console.log('기존 자료실 게시글:', legacyPostsData.length, '개');
    });

    // 영문 자료실 게시글 로드
    const enPostsRef = collection(db, 'en-posts');
    const enPostsQuery = query(enPostsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeEnPosts = onSnapshot(enPostsQuery, (snapshot) => {
      const enPostsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEnPosts(enPostsData);
    });

    // 한글 전자구매 게시글 로드
    const koEProcurementRef = collection(db, 'ko-e-procurement');
    const koEProcurementQuery = query(koEProcurementRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeKoEProcurement = onSnapshot(koEProcurementQuery, (snapshot) => {
      const koEProcurementData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setKoEProcurementPosts(koEProcurementData);
    });

    // 영문 전자구매 게시글 로드
    const enEProcurementRef = collection(db, 'en-e-procurement');
    const enEProcurementQuery = query(enEProcurementRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeEnEProcurement = onSnapshot(enEProcurementQuery, (snapshot) => {
      const enEProcurementData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEnEProcurementPosts(enEProcurementData);
    });

    // 기존 e-procurement 컬렉션 로드 (별도 탭으로 관리)
    const legacyEProcurementRef = collection(db, 'e-procurement');
    const legacyEProcurementQuery = query(legacyEProcurementRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeLegacyEProcurement = onSnapshot(legacyEProcurementQuery, (snapshot) => {
      const legacyEProcurementData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLegacyEProcurementPosts(legacyEProcurementData);
      console.log('기존 전자구매 게시글:', legacyEProcurementData.length, '개');
    });

    return () => {
      unsubscribeKoPosts();
      unsubscribeLegacyPosts();
      unsubscribeEnPosts();
      unsubscribeKoEProcurement();
      unsubscribeEnEProcurement();
      unsubscribeLegacyEProcurement();
    };
  }, []);

  // 메시지 표시 함수
  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  // 관리자 설정 초기화 (초기 비밀번호 설정)
  useEffect(() => {
    const initializeAdminSettings = async () => {
      try {
        const adminSettingsRef = doc(db, 'admin-settings', 'password');
        const adminSettingsSnap = await getDoc(adminSettingsRef);
        
        if (!adminSettingsSnap.exists()) {
          // 초기 비밀번호 설정
          await setDoc(adminSettingsRef, {
            password: '1234',
            createdAt: new Date(),
            updatedAt: new Date()
          });
          console.log('초기 관리자 비밀번호가 설정되었습니다: 1234');
        }
      } catch (error) {
        console.error('관리자 설정 초기화 중 오류:', error);
      }
    };
    
    initializeAdminSettings();
  }, []);

  // 로그인 처리
  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const adminSettingsRef = doc(db, 'admin-settings', 'password');
      const adminSettingsSnap = await getDoc(adminSettingsRef);
      
      if (adminSettingsSnap.exists()) {
        const adminData = adminSettingsSnap.data();
        if (adminData.password === loginPassword) {
          setIsLoggedIn(true);
          setLoginPassword('');
          showMessage('로그인 성공!', 'success');
        } else {
          showMessage('비밀번호가 올바르지 않습니다.', 'error');
        }
      } else {
        showMessage('관리자 설정을 찾을 수 없습니다.', 'error');
      }
    } catch (error) {
      console.error('로그인 중 오류:', error);
      showMessage('로그인 중 오류가 발생했습니다.', 'error');
    }
  };

  // 로그아웃 처리
  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowPasswordChange(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // 비밀번호 변경 처리
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showMessage('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.', 'error');
      return;
    }
    
    if (newPassword.length < 4) {
      showMessage('비밀번호는 최소 4자 이상이어야 합니다.', 'error');
      return;
    }
    
    try {
      const adminSettingsRef = doc(db, 'admin-settings', 'password');
      const adminSettingsSnap = await getDoc(adminSettingsRef);
      
      if (adminSettingsSnap.exists()) {
        const adminData = adminSettingsSnap.data();
        if (adminData.password === currentPassword) {
          await updateDoc(adminSettingsRef, {
            password: newPassword,
            updatedAt: new Date()
          });
          
          showMessage('비밀번호가 성공적으로 변경되었습니다.', 'success');
          setShowPasswordChange(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } else {
          showMessage('현재 비밀번호가 올바르지 않습니다.', 'error');
        }
      }
    } catch (error) {
      console.error('비밀번호 변경 중 오류:', error);
      showMessage('비밀번호 변경 중 오류가 발생했습니다.', 'error');
    }
  };

  // 파일 업로드 처리
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      const folder = getCollectionNameByTab(activeTab);
      showMessage('PDF 업로드 중...', 'info');
      uploadPdfToStorage({ file, folder })
        .then(({ url }) => {
          setFormData((prev) => ({
            ...prev,
            pdfFileName: file.name,
            pdfUrl: url,
          }));
          showMessage('PDF 업로드 완료!', 'success');
        })
        .catch((error) => {
          console.error('PDF 업로드 실패:', error);
          showMessage('PDF 업로드에 실패했습니다.', 'error');
        });
    } else {
      showMessage('PDF 파일만 업로드 가능합니다.', 'error');
    }
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      showMessage('제목과 내용을 입력해주세요.', 'error');
      return;
    }

    try {
      const postData = {
        title: formData.title,
        content: formData.content,
        pdfFileName: formData.pdfFileName,
        pdfUrl: formData.pdfUrl || '',
        password: formData.password,
        language: formData.language,
        category: (activeTab === 'ko-posts' || activeTab === 'en-posts')
          ? (formData.category || 'catalog')
          : undefined,
        createdAt: editingPost ? editingPost.createdAt : new Date(),
        updatedAt: new Date()
      };

      // 언어별 컬렉션 선택
      const collectionName = getCollectionNameByTab(activeTab);
      
      if (editingPost) {
        // 게시글 수정
        const postRef = doc(db, collectionName, editingPost.id);
        await updateDoc(postRef, {
          title: formData.title,
          content: formData.content,
          pdfFileName: formData.pdfFileName,
          pdfUrl: formData.pdfUrl || '',
          password: formData.password,
          ...(activeTab === 'ko-posts' || activeTab === 'en-posts'
            ? { category: formData.category || 'catalog' }
            : {}),
          updatedAt: new Date()
        });
        showMessage('게시글이 수정되었습니다.', 'success');
      } else {
        // 새 게시글 추가
        const cleanedPostData = Object.fromEntries(
          Object.entries(postData).filter(([, v]) => v !== undefined && v !== '')
        );
        await addDoc(collection(db, collectionName), cleanedPostData);
        showMessage('게시글이 등록되었습니다.', 'success');
      }

      resetForm();
    } catch (error) {
      console.error('게시글 저장 중 오류:', error);
      showMessage('게시글 저장 중 오류가 발생했습니다.', 'error');
    }
  };

  // 폼 리셋
  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      pdfFileName: '',
      pdfUrl: '',
      password: '',
      language: activeTab.includes('en') ? 'en' : 'ko',
      category: 'catalog'
    });
    setShowForm(false);
    setEditingPost(null);
  };

  // 게시글 수정
  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      content: post.content,
      pdfFileName: post.pdfFileName,
      pdfUrl: post.pdfUrl || '',
      password: post.password || '',
      language: post.language || (activeTab.includes('en') ? 'en' : 'ko'),
      category: post.category || 'catalog'
    });
    setEditingPost(post);
    setShowForm(true);
  };

  // 게시글 삭제
  const handleDelete = async (postId) => {
    const confirmDelete = window.confirm('정말로 삭제하시겠습니까?');
    if (!confirmDelete) return;
    
    try {
      // 언어별 컬렉션 선택
      let collectionName;
      if (activeTab === 'ko-posts') collectionName = 'ko-posts';
      else if (activeTab === 'en-posts') collectionName = 'en-posts';
      else if (activeTab === 'ko-e-procurement') collectionName = 'ko-e-procurement';
      else if (activeTab === 'en-e-procurement') collectionName = 'en-e-procurement';
      else if (activeTab === 'legacy-posts') collectionName = 'posts';
      else if (activeTab === 'legacy-e-procurement') collectionName = 'e-procurement';
      
      await deleteDoc(doc(db, collectionName, postId));
      showMessage('게시글이 삭제되었습니다.', 'success');
    } catch (error) {
      console.error('게시글 삭제 중 오류:', error);
      showMessage('게시글 삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  // PDF 다운로드
  const handleDownload = (post) => {
    if (post.pdfUrl) {
      window.open(post.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (post.pdfFile) {
      const link = document.createElement('a');
      link.href = post.pdfFile;
      link.download = post.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 현재 활성 탭의 게시글 목록 가져오기
  // 현재 탭에 따른 게시글 선택
  const getCurrentPosts = () => {
    switch(activeTab) {
      case 'ko-posts': return koPosts;
      case 'en-posts': return enPosts;
      case 'ko-e-procurement': return koEProcurementPosts;
      case 'en-e-procurement': return enEProcurementPosts;
      case 'legacy-posts': return legacyPosts;
      case 'legacy-e-procurement': return legacyEProcurementPosts;
      default: return koPosts;
    }
  };
  
  const currentPosts = getCurrentPosts();
  const isDocCenterTab = activeTab === 'ko-posts' || activeTab === 'en-posts';
  const filteredCurrentPosts = isDocCenterTab && postCategoryFilter !== 'all'
    ? currentPosts.filter((p) => (p.category || 'catalog') === postCategoryFilter)
    : currentPosts;

  const isPostTab =
    activeTab === 'ko-posts' ||
    activeTab === 'en-posts' ||
    activeTab === 'ko-e-procurement' ||
    activeTab === 'en-e-procurement' ||
    activeTab === 'legacy-posts' ||
    activeTab === 'legacy-e-procurement';

  // 로그인하지 않은 경우 로그인 폼 표시
  if (!isLoggedIn) {
    return (
      <div className="admin-container">
        <div className="login-container">
          <div className="login-form">
            <h1>관리자 로그인</h1>
            {message && (
              <div className={`message ${messageType}`}>
                {message}
              </div>
            )}
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>비밀번호</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="관리자 비밀번호를 입력하세요"
                  required
                />
              </div>
              <button type="submit" className="btn-primary">
                로그인
              </button>
            </form>
            <div className="login-info">
              <p>로그인 후 비밀번호를 변경할 수 있습니다.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}
      <div className="admin-header">
        <h1>관리자 페이지</h1>
        <div className="admin-actions">
          <button 
            className="btn-secondary"
            onClick={() => setShowPasswordChange(true)}
          >
            비밀번호 변경
          </button>
          {isPostTab && (
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              새 게시글 작성
            </button>
          )}
          <button 
            className="btn-logout"
            onClick={handleLogout}
          >
            로그아웃
          </button>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'ko-posts' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('ko-posts');
            setFormData({...formData, language: 'ko'});
            setPostCategoryFilter('all');
          }}
        >
          📋 한글 자료실
        </button>
        <button 
          className={`tab-btn ${activeTab === 'en-posts' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('en-posts');
            setFormData({...formData, language: 'en'});
            setPostCategoryFilter('all');
          }}
        >
          📋 영문 자료실
        </button>
        <button 
          className={`tab-btn ${activeTab === 'ko-e-procurement' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('ko-e-procurement');
            setFormData({...formData, language: 'ko'});
          }}
        >
          🛒 한글 전자구매
        </button>
        <button 
          className={`tab-btn ${activeTab === 'en-e-procurement' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('en-e-procurement');
            setFormData({...formData, language: 'en'});
          }}
        >
          🛒 영문 전자구매
        </button>
        <button
          className={`tab-btn ${activeTab === 'delivery-files' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('delivery-files');
            setShowForm(false);
            setEditingPost(null);
          }}
        >
          📦 납품실적 엑셀
        </button>
        <button
          className={`tab-btn ${activeTab === 'certificates' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('certificates');
            setShowForm(false);
            setEditingPost(null);
          }}
        >
          📄 인증서
        </button>
      </div>

      {/* 게시글 작성/수정 폼 */}
      {isPostTab && showForm && (
        <div className="form-modal">
          <div className="form-content">
            <div className="form-header">
              <h2>{editingPost ? '게시글 수정' : '새 게시글 작성'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            
             <form onSubmit={handleSubmit}>
               <div style={{ marginBottom: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
                 저장 위치: {
                   activeTab === 'ko-posts' ? '한글 자료실' :
                   activeTab === 'en-posts' ? '영문 자료실' :
                   activeTab === 'ko-e-procurement' ? '한글 전자구매' :
                   activeTab === 'en-e-procurement' ? '영문 전자구매' :
                   activeTab
                 }
                 {(activeTab === 'ko-posts' || activeTab === 'en-posts') && (
                   <> / 카테고리: {formData.category === 'technical' ? '기술자료' : '카탈로그'}</>
                 )}
               </div>

               <div className="form-group">
                <label>제목</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="게시글 제목을 입력하세요"
                  required
                />
              </div>

              {(activeTab === 'ko-posts' || activeTab === 'en-posts') && (
                <div className="form-group">
                  <label>카테고리</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="catalog">카탈로그</option>
                    <option value="technical">기술자료</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>내용</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="게시글 내용을 입력하세요"
                  rows="10"
                  required
                />
              </div>

              <div className="form-group">
                <label>PDF 파일 업로드</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
                {formData.pdfFileName && (
                  <p className="file-info">선택된 파일: {formData.pdfFileName}</p>
                )}
                {formData.pdfUrl && (
                  <p className="file-info">
                    업로드 URL:&nbsp;
                    <a href={formData.pdfUrl} target="_blank" rel="noreferrer">
                      열기
                    </a>
                  </p>
                )}
              </div>

              {/* 전자구매 탭에서만 비밀번호 필드 표시 */}
              {activeTab.includes('e-procurement') && (
                <div className="form-group">
                  <label>비밀번호 (선택사항)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="비밀번호를 입력하면 게시글 열람 시 비밀번호가 필요합니다"
                  />
                  <p className="form-help">비밀번호를 설정하면 게시글 열람 시 비밀번호 입력이 필요합니다. 비워두면 비밀번호 없이 열람 가능합니다.</p>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn-secondary">
                  취소
                </button>
                <button type="submit" className="btn-primary">
                  {editingPost ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 비밀번호 변경 모달 */}
      {showPasswordChange && (
        <div className="form-modal">
          <div className="form-content">
            <div className="form-header">
              <h2>비밀번호 변경</h2>
              <button className="close-btn" onClick={() => setShowPasswordChange(false)}>×</button>
            </div>
            
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>현재 비밀번호</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="현재 비밀번호를 입력하세요"
                  required
                />
              </div>

              <div className="form-group">
                <label>새 비밀번호</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호를 입력하세요 (최소 4자)"
                  required
                  minLength="4"
                />
              </div>

              <div className="form-group">
                <label>새 비밀번호 확인</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력하세요"
                  required
                  minLength="4"
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowPasswordChange(false)} className="btn-secondary">
                  취소
                </button>
                <button type="submit" className="btn-primary">
                  비밀번호 변경
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 탭별 컨텐츠 */}
      {activeTab === 'delivery-files' && (
        <div className="posts-list">
          <DeliveryFilesManagement />
        </div>
      )}

      {activeTab === 'certificates' && (
        <div className="posts-list">
          <CertificationManagement />
        </div>
      )}

      {isPostTab && (
      <div className="posts-list">
        {isDocCenterTab && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
            <select
              value={postCategoryFilter}
              onChange={(e) => setPostCategoryFilter(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #ddd',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              <option value="all">전체</option>
              <option value="catalog">카탈로그</option>
              <option value="technical">기술자료</option>
            </select>
          </div>
        )}
        <h2>{
          activeTab === 'ko-posts' ? `한글 자료실 게시글 관리 (${filteredCurrentPosts.length}개)` :
          activeTab === 'en-posts' ? `영문 자료실 게시글 관리 (${filteredCurrentPosts.length}개)` :
          activeTab === 'ko-e-procurement' ? `한글 전자구매 게시글 관리 (${filteredCurrentPosts.length}개)` :
          activeTab === 'en-e-procurement' ? `영문 전자구매 게시글 관리 (${filteredCurrentPosts.length}개)` :
          activeTab === 'legacy-posts' ? `기존 자료실 게시글 관리 (${filteredCurrentPosts.length}개)` :
          activeTab === 'legacy-e-procurement' ? `기존 전자구매 게시글 관리 (${filteredCurrentPosts.length}개)` :
          `게시글 관리 (${filteredCurrentPosts.length}개)`
        }</h2>
        {filteredCurrentPosts.length === 0 ? (
          <p className="no-posts">등록된 게시글이 없습니다.</p>
        ) : (
          <div className="posts-grid">
            {filteredCurrentPosts.map(post => (
              <div key={post.id} className="post-card">
                <div className="post-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <h3 style={{ margin: 0, marginRight: '0.5rem' }}>{post.title}</h3>
                    {isDocCenterTab && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 999,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          border: '1px solid #e5e7eb',
                          background:
                            (post.category || 'catalog') === 'technical'
                              ? '#fff7ed'
                              : '#eff6ff',
                          color:
                            (post.category || 'catalog') === 'technical'
                              ? '#9a3412'
                              : '#1d4ed8',
                          whiteSpace: 'nowrap',
                        }}
                        title="자료실 카테고리"
                      >
                        {(post.category || 'catalog') === 'technical' ? '기술자료' : '카탈로그'}
                      </span>
                    )}
                  </div>
                  <div className="post-actions">
                    <button onClick={() => handleEdit(post)} className="btn-edit">
                      수정
                    </button>
                    <button onClick={() => handleDelete(post.id)} className="btn-delete">
                      삭제
                    </button>
                  </div>
                </div>
                <div className="post-content">
                  <p>{post.content.substring(0, 100)}...</p>
                </div>
                <div className="post-footer">
                  <div className="post-date">
                    등록일: {
                      post.createdAt ? 
                        new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString('ko-KR') : 
                        '날짜 없음'
                    }
                  </div>
                  {post.pdfFileName && (
                    <div className="post-file">
                      <span>📄 {post.pdfFileName}</span>
                      <button 
                        onClick={() => handleDownload(post)}
                        className="btn-download"
                      >
                        다운로드
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Admin;
